import { apiBase } from './assets.js';

/**
 * Eşleştirme sayfası (Faz 1.14) — TV/ekran ilk açılışta cihaz token'ı yoksa burası gösterilir.
 *
 * Akış: kullanıcı panelden 6 haneli eşleşme kodu üretir → bu ekrana girer →
 * POST /api/public/devices/pair → dönen JWT localStorage'a yazılır → '/' yeniden
 * yüklenir, bootLiveMode devralır.
 *
 * `?code=123456` ile gelinirse otomatik denenir (panel pair_url linki).
 */
interface PairResponse {
    device_id: string;
    tenant_id: string;
    branch_id: string;
    token: string;
    expires_in_sec: number;
    gateway_url: string;
    diagnostic_pin?: string; // tanı ekranı PIN'i (varsa) — K4
}

const REASON_MESSAGES: Record<string, string> = {
    token_expired: 'Ekranın oturum süresi doldu. Panelden yeni bir eşleme kodu üretip aşağıya girin.',
    token_invalidated: 'Bu ekranın bağlantısı panelden kaldırıldı. Yeni bir eşleme kodu girin.',
    token_invalid: 'Ekranın bağlantı bilgisi geçersiz. Panelden yeni bir eşleme kodu girin.',
    device_removed: 'Bu ekran panelden kaldırıldı. Yeniden yayın için yeni bir eşleme kodu girin.',
    device_inactive: 'Bu ekran panelde pasife alındı. Yeniden etkinleştirmek için yöneticinizle görüşün veya yeni kod girin.',
};

export async function bootPairMode(app: HTMLElement): Promise<void> {
    const { connectionState } = await import('./state/connection-state.js');
    connectionState.set('online');

    const sp = new URLSearchParams(location.search);
    const prefill = sp.get('code') ?? '';
    const reason = sp.get('reason') ?? '';
    const reasonMsg = REASON_MESSAGES[reason] ?? '';
    const offline = navigator.onLine === false;

    app.innerHTML = `
        <div class="fp-pair">
            <div class="fp-pair-card">
                <h1 class="fp-pair-title">Ekranı Eşleştir</h1>
                ${reasonMsg ? `<p class="fp-pair-note">${reasonMsg}</p>` : ''}
                ${offline ? `<p class="fp-pair-note">İnternet bağlantısı yok. Eşleme için ekranı internete bağlayın.</p>` : ''}
                <p class="fp-pair-sub">Yönetim panelinizden (panel.fiyatpanom.com → Ekranlar → Yeni Ekran)
                    aldığınız 6 haneli kodu girin. Kod kısa süre geçerlidir.</p>
                <input id="fp-pair-input" class="fp-pair-input" inputmode="numeric" autocomplete="off"
                       maxlength="6" pattern="[0-9]*" placeholder="------" value="${prefill.replace(/[^0-9]/g, '').slice(0, 6)}">
                <button id="fp-pair-submit" class="fp-pair-btn">Eşleştir</button>
                <p id="fp-pair-status" class="fp-pair-status"></p>
                <p class="fp-pair-help">Kod alamıyorsanız işletmenizin yöneticisiyle veya destek ekibiyle görüşün.</p>
            </div>
        </div>
        <style>
            .fp-pair { position:fixed; inset:0; display:flex; align-items:center; justify-content:center;
                       background:#0f172a; color:#e2e8f0; font-family:system-ui,-apple-system,sans-serif; }
            .fp-pair-card { text-align:center; padding:3rem; max-width:560px; width:90%; }
            .fp-pair-title { font-size:2.4rem; margin:0 0 .5rem; color:#C9A961; }
            .fp-pair-note { font-size:1.1rem; color:#fbbf24; margin:0 0 1rem; line-height:1.4; }
            .fp-pair-sub { font-size:1.15rem; color:#94a3b8; margin:0 0 2rem; line-height:1.4; }
            .fp-pair-help { font-size:.95rem; color:#64748b; margin:1.5rem 0 0; }
            .fp-pair-input { font-size:3rem; letter-spacing:.6rem; text-align:center; width:100%;
                             padding:1rem; border-radius:.75rem; border:2px solid #334155; background:#1e293b;
                             color:#fff; box-sizing:border-box; }
            .fp-pair-input:focus { outline:none; border-color:#C9A961; }
            .fp-pair-btn { margin-top:1.5rem; font-size:1.4rem; padding:1rem 2rem; width:100%;
                           border:none; border-radius:.75rem; background:#C9A961; color:#0f172a;
                           font-weight:700; cursor:pointer; }
            .fp-pair-btn:disabled { opacity:.6; cursor:default; }
            .fp-pair-status { margin-top:1.25rem; font-size:1.1rem; min-height:1.5rem; }
            .fp-pair-status.err { color:#f87171; }
            .fp-pair-status.ok { color:#34d399; }
        </style>
    `;

    const input = app.querySelector<HTMLInputElement>('#fp-pair-input')!;
    const btn = app.querySelector<HTMLButtonElement>('#fp-pair-submit')!;
    const status = app.querySelector<HTMLParagraphElement>('#fp-pair-status')!;

    input.addEventListener('input', () => {
        input.value = input.value.replace(/[^0-9]/g, '').slice(0, 6);
        // 6. rakam girilince otomatik eşleştir (TV kumandasıyla buton aramak zor) (L6).
        if (input.value.length === 6) void submit();
    });
    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') void submit();
    });
    btn.addEventListener('click', () => void submit());

    async function submit(): Promise<void> {
        const code = input.value.trim();
        if (code.length !== 6) {
            status.className = 'fp-pair-status err';
            status.textContent = 'Lütfen 6 haneli kodu girin.';
            return;
        }

        btn.disabled = true;
        status.className = 'fp-pair-status';
        status.textContent = 'Eşleştiriliyor…';

        try {
            const res = await fetch(`${apiBase()}/api/public/devices/pair`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    code,
                    client_info: {
                        platform: 'web',
                        user_agent: navigator.userAgent,
                        client_version: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'pwa',
                    },
                }),
            });

            if (!res.ok) {
                status.className = 'fp-pair-status err';
                status.textContent = res.status === 422
                    ? 'Kod geçersiz veya süresi dolmuş. Panelden yeni kod üretin.'
                    : `Eşleştirme başarısız (${res.status}). Tekrar deneyin.`;
                btn.disabled = false;
                return;
            }

            const data = (await res.json()) as PairResponse;
            localStorage.setItem('fp.device.token', data.token);
            if (data.gateway_url) {
                localStorage.setItem('fp.gateway.url', data.gateway_url);
            }
            // Tanı ekranı PIN'i — varsa yaz; yoksa diagnostic salt-görüntüleme açılır (K4).
            if (data.diagnostic_pin) {
                localStorage.setItem('fp.diagnostic.pin', data.diagnostic_pin);
            }

            status.className = 'fp-pair-status ok';
            status.textContent = 'Eşleştirildi! Yayın başlıyor…';
            setTimeout(() => location.replace('/'), 800);
        } catch {
            status.className = 'fp-pair-status err';
            status.textContent = 'Bağlantı hatası. İnternet bağlantısını kontrol edin.';
            btn.disabled = false;
        }
    }

    // ?code= ile gelindiyse otomatik dene
    if (/^[0-9]{6}$/.test(input.value)) {
        void submit();
    } else {
        input.focus();
    }
}
